import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS, hasPermission } from '../../auth/permissions';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import { logError } from '../../utils/logError';
import { extractApiError } from '../../utils/apiError';
import { parseTutarTR, formatTutarTR } from '../../utils/para';
import { colors, radius, shadows } from '../../styles/theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ISO (yyyy-MM-dd) → dd.MM.yyyy. Boş/eksik tarih için "—".
const fmtTarih = (iso) => {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const fmt = (v) => formatTutarTR(v || 0);

const todayISO = () => new Date().toISOString().split('T')[0];

const thStyle = (align = 'left') => ({
  padding: '10px 14px', textAlign: align, fontSize: '11px', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.06em', color: colors.textTertiary,
  whiteSpace: 'nowrap',
});

// Yaklaşan ödeme radarı: son_odeme_gunu bugüne göre kaç gün uzakta?
// Bu ayki son ödeme günü geçmişse gelecek aya taşı. Borç yoksa radar yok.
const yaklasanGun = (sonOdemeGunu, acikBorc) => {
  if (!sonOdemeGunu || !acikBorc || acikBorc <= 0) return null;
  const bugun = new Date();
  let y = bugun.getFullYear();
  let m = bugun.getMonth();
  const clampGun = (yil, ay) => Math.min(sonOdemeGunu, new Date(yil, ay + 1, 0).getDate());
  let hedef = new Date(y, m, clampGun(y, m));
  hedef.setHours(0, 0, 0, 0);
  const ref = new Date(y, m, bugun.getDate());
  ref.setHours(0, 0, 0, 0);
  if (hedef < ref) {
    m += 1;
    if (m > 11) { m = 0; y += 1; }
    hedef = new Date(y, m, clampGun(y, m));
    hedef.setHours(0, 0, 0, 0);
  }
  return Math.round((hedef - ref) / 86400000);
};

const KrediKartlariPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const canManage = hasPermission(user?.role, PERMISSIONS.MANAGE_CREDIT_CARDS, user?.effectivePermissions);

  const [ozet, setOzet] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detay, setDetay] = useState(null);          // { kart, acik_alislar, acik_borc, ... }
  const [detayLoading, setDetayLoading] = useState(false);

  // Seçili kart URL'de (geri=korunur, link paylaşılabilir).
  const [seciliKartId, setSeciliKartId] = useUrlState('kart', '');

  // Ekstre öde modal state
  const [odemeModal, setOdemeModal] = useState(null);  // { donem_bitis, odenen_tutar, odeme_tarihi, hesap_etiketi, aciklama }
  const [saving, setSaving] = useState(false);
  const [iptalInFlight, setIptalInFlight] = useState(() => new Set());

  const fetchOzet = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/kredi-karti/ozet`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setOzet(Array.isArray(data) ? data : []);
    } catch (e) {
      logError(e);
      toast.error('Kredi kartı özeti yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchDetay = useCallback(async (kartId) => {
    if (!kartId) { setDetay(null); return; }
    setDetayLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kredi-karti/${kartId}/ekstre`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDetay(data);
    } catch (e) {
      logError(e);
      toast.error('Kart ekstresi yüklenemedi');
      setDetay(null);
    } finally {
      setDetayLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchOzet(); }, [fetchOzet]);
  useEffect(() => { fetchDetay(seciliKartId); }, [seciliKartId, fetchDetay]);

  const toplamBorc = useMemo(() => ozet.reduce((s, k) => s + (k.acik_borc || 0), 0), [ozet]);
  const yaklasanSayisi = useMemo(
    () => ozet.filter(k => {
      const g = yaklasanGun(k.son_odeme_gunu, k.acik_borc);
      return g !== null && g <= 7;
    }).length,
    [ozet]
  );

  const seciliKart = useMemo(
    () => ozet.find(k => String(k.id) === String(seciliKartId)) || null,
    [ozet, seciliKartId]
  );

  // Ekstre Öde modalını aç — önerilen kesim + ekstre tutarı detaydan otomatik dolar.
  const openOdemeModal = () => {
    if (!detay) return;
    setOdemeModal({
      donem_bitis: detay.onerilen_kesim || todayISO(),
      odenen_tutar: formatTutarTR(detay.ekstre_tutari || 0),
      odeme_tarihi: todayISO(),
      hesap_etiketi: '',
      aciklama: '',
    });
  };

  const submitOdeme = async () => {
    if (!odemeModal || !seciliKartId) return;
    const tutar = parseTutarTR(odemeModal.odenen_tutar);
    if (!odemeModal.donem_bitis) { toast.warning('Dönem (kesim) tarihi zorunludur'); return; }
    if (tutar === null || tutar < 0) { toast.warning('Ödenen tutar geçersiz'); return; }
    if (!odemeModal.odeme_tarihi) { toast.warning('Ödeme tarihi zorunludur'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/kredi-karti/${seciliKartId}/ekstre-ode`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          donem_bitis: odemeModal.donem_bitis,
          odenen_tutar: tutar,
          odeme_tarihi: odemeModal.odeme_tarihi,
          hesap_etiketi: odemeModal.hesap_etiketi || null,
          aciklama: odemeModal.aciklama || null,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(extractApiError(e.detail, 'Ekstre ödemesi kaydedilemedi'));
      }
      toast.success('Ekstre ödemesi kaydedildi');
      setOdemeModal(null);
      await Promise.all([fetchOzet(), fetchDetay(seciliKartId)]);
    } catch (e) {
      logError(e);
      toast.error(e.message || 'Ekstre ödemesi kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const iptalEt = async (odeme) => {
    if (iptalInFlight.has(odeme.id)) return;
    setIptalInFlight(prev => new Set(prev).add(odeme.id));
    try {
      const res = await fetch(`${API_URL}/api/kredi-karti/ekstre-odeme/${odeme.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(extractApiError(e.detail, 'Ödeme iptal edilemedi'));
      }
      toast.success('Ekstre ödemesi iptal edildi (borç geri açıldı)');
      await Promise.all([fetchOzet(), fetchDetay(seciliKartId)]);
    } catch (e) {
      logError(e);
      toast.error(e.message || 'Ödeme iptal edilemedi');
    } finally {
      setIptalInFlight(prev => { const n = new Set(prev); n.delete(odeme.id); return n; });
    }
  };

  const kartBaslik = (k) => k.banka ? `${k.banka} •••• ${k.son_dort_hane}` : `•••• ${k.son_dort_hane}`;

  if (loading && ozet.length === 0) {
    return (
      <div style={{ padding: '20px' }}>
        <h2 style={{ margin: '0 0 20px', color: colors.textPrimary, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em' }}>Kredi Kartları</h2>
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '24px', color: colors.textTertiary }}>
          Yükleniyor…
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ margin: '0 0 6px', color: colors.textPrimary, fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em' }}>
        Kredi Kartları
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: '12px', color: colors.textSecondary, maxWidth: '720px' }}>
        Kart alışları gider girişinden düşer (P&amp;L'e alış tarihinde). Ekstre ödemesi birikmiş kart borcunu
        kapatan bir <strong>nakit hareketidir</strong> — gider olarak ikinci kez kaydedilmez.
      </p>

      {/* KPI kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.primary}`, padding: '16px', boxShadow: shadows.sm }}>
          <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Toplam Kart Borcu
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
            {fmt(toplamBorc)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
          </div>
        </div>
        <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${yaklasanSayisi > 0 ? colors.warning : colors.border}`, padding: '16px', boxShadow: shadows.sm }}>
          <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Yaklaşan Ödeme (7 gün)
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: yaklasanSayisi > 0 ? colors.warning : colors.textPrimary }}>
            {yaklasanSayisi} kart
          </div>
        </div>
        <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.info}`, padding: '16px', boxShadow: shadows.sm }}>
          <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
            Aktif Kart
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
            {ozet.length}
          </div>
        </div>
      </div>

      {/* Kart listesi */}
      <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'auto', marginBottom: '24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
          <thead>
            <tr style={{ background: colors.backgroundAlt }}>
              <th style={thStyle()}>Kart</th>
              <th style={thStyle('right')}>Açık Borç</th>
              <th style={thStyle('right')}>Hareket</th>
              <th style={thStyle()}>Son Ödeme Günü</th>
              <th style={thStyle()}>Yaklaşan</th>
              <th style={thStyle()} />
            </tr>
          </thead>
          <tbody>
            {ozet.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '32px 16px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>
                  Aktif kredi kartı yok. Ayarlar → Kredi Kartları'ndan kart ekleyin.
                </td>
              </tr>
            ) : ozet.map(k => {
              const gun = yaklasanGun(k.son_odeme_gunu, k.acik_borc);
              const acik = String(k.id) === String(seciliKartId);
              const radar = gun !== null && gun <= 7;
              return (
                <tr
                  key={k.id}
                  onClick={() => setSeciliKartId(acik ? '' : String(k.id))}
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                    background: acik ? colors.primaryGhost : 'transparent',
                    cursor: 'pointer',
                  }}>
                  <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    {kartBaslik(k)}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '14px', fontWeight: 700, color: (k.acik_borc || 0) > 0 ? colors.primary : colors.textTertiary, fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(k.acik_borc)} <span style={{ fontSize: '11px', fontWeight: 500, color: colors.textTertiary }}>TL</span>
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '13px', color: colors.textSecondary }}>
                    {k.acik_alis_adedi || 0}
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: '13px', color: colors.textSecondary }}>
                    {k.son_odeme_gunu ? `Her ayın ${k.son_odeme_gunu}.` : <span style={{ color: colors.textTertiary }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: '12px' }}>
                    {gun === null ? (
                      <span style={{ color: colors.textTertiary }}>—</span>
                    ) : (
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: radius.full,
                        fontSize: '11px', fontWeight: 600,
                        background: radar ? colors.warningLight : colors.backgroundAlt,
                        color: radar ? '#92400e' : colors.textSecondary,
                      }}>
                        {gun === 0 ? 'Bugün' : `${gun} gün`}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontSize: '12px', color: colors.primary, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {acik ? 'Gizle ▲' : 'Detay ▼'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Kart detayı */}
      {seciliKartId && (
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, padding: '20px', marginBottom: '24px' }}>
          {detayLoading && !detay ? (
            <div style={{ color: colors.textTertiary, fontSize: '13px' }}>Ekstre yükleniyor…</div>
          ) : !detay ? (
            <div style={{ color: colors.textTertiary, fontSize: '13px' }}>Ekstre verisi yok.</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: colors.textPrimary }}>
                    {seciliKart ? kartBaslik(seciliKart) : (detay.kart ? `•••• ${detay.kart.son_dort_hane}` : 'Kart')}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>
                    Açık dönem: {fmtTarih(detay.acik_donem_baslangic)} – {fmtTarih(detay.onerilen_kesim)}
                    {detay.covered_through && <> · Kapanan dönem sonu: {fmtTarih(detay.covered_through)}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase' }}>Ekstre Tutarı</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, color: (detay.ekstre_tutari || 0) > 0 ? colors.primary : colors.textPrimary }}>
                      {fmt(detay.ekstre_tutari)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
                    </div>
                  </div>
                  {canManage && (
                    <button
                      onClick={openOdemeModal}
                      disabled={(detay.acik_alislar || []).length === 0}
                      title={(detay.acik_alislar || []).length === 0 ? 'Açık hareket yok — ödenecek ekstre bulunmuyor' : 'Ekstre öde'}
                      style={{
                        padding: '10px 18px', background: (detay.acik_alislar || []).length === 0 ? colors.border : colors.primary,
                        color: '#fff', border: 'none', borderRadius: radius.sm,
                        cursor: (detay.acik_alislar || []).length === 0 ? 'not-allowed' : 'pointer',
                        fontSize: '13px', fontWeight: 700,
                      }}>
                      Ekstre Öde
                    </button>
                  )}
                </div>
              </div>

              {/* Açık dönem hareketleri (kart alışı + tedarikçi kart ödemesi birleşik) */}
              <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textPrimary, margin: '8px 0 8px' }}>
                Açık Dönem Hareketleri ({(detay.acik_alislar || []).length})
              </div>
              <div style={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm, marginBottom: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt }}>
                      <th style={thStyle()}>Tarih</th>
                      <th style={thStyle()}>Tür</th>
                      <th style={thStyle()}>Açıklama</th>
                      <th style={thStyle()}>Firma</th>
                      <th style={thStyle('right')}>Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detay.acik_alislar || []).length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>Açık (ödenmemiş) hareket yok.</td></tr>
                    ) : (detay.acik_alislar || []).map(a => {
                      const odeme = a.tip === 'TEDARIKCI_ODEME';
                      return (
                        <tr key={`${a.kaynak || 'h'}-${a.id}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                          <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 500 }}>{fmtTarih(a.tarih)}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: radius.sm,
                              fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
                              background: odeme ? '#fff4e5' : '#eef4ff',
                              color: odeme ? '#9a5b00' : '#1d4ed8',
                            }}>
                              {odeme ? 'Tedarikçi Ödemesi' : 'Alış'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: '13px', color: colors.textPrimary }}>{a.etiket || '—'}</td>
                          <td style={{ padding: '10px 14px', fontSize: '12px', color: colors.textSecondary }}>{a.firma || '—'}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmt(a.tutar)} TL</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Geçmiş ödemeler */}
              <div style={{ fontSize: '13px', fontWeight: 700, color: colors.textPrimary, margin: '8px 0 8px' }}>
                Geçmiş Ekstre Ödemeleri ({(detay.gecmis_odemeler || []).length})
              </div>
              <div style={{ overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt }}>
                      <th style={thStyle()}>Dönem</th>
                      <th style={thStyle()}>Ödeme Tarihi</th>
                      <th style={thStyle()}>Hesap</th>
                      <th style={thStyle('right')}>Ekstre</th>
                      <th style={thStyle('right')}>Ödenen</th>
                      {canManage && <th style={thStyle('right')} />}
                    </tr>
                  </thead>
                  <tbody>
                    {(detay.gecmis_odemeler || []).length === 0 ? (
                      <tr><td colSpan={canManage ? 6 : 5} style={{ padding: '20px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>Henüz ekstre ödemesi yok.</td></tr>
                    ) : (detay.gecmis_odemeler || []).map(o => (
                      <tr key={o.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '10px 14px', fontSize: '13px' }}>{fmtTarih(o.donem_baslangic)} – {fmtTarih(o.donem_bitis)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '13px', fontWeight: 500 }}>{fmtTarih(o.odeme_tarihi)}</td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: colors.textSecondary }}>
                          {o.hesap_etiketi || '—'}{o.firma ? ` · ${o.firma}` : ''}
                        </td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', color: colors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{fmt(o.ekstre_tutari)}</td>
                        <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: colors.success, fontVariantNumeric: 'tabular-nums' }}>{fmt(o.odenen_tutar)}</td>
                        {canManage && (
                          <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                            <button
                              onClick={() => iptalEt(o)}
                              disabled={iptalInFlight.has(o.id)}
                              title="Ödemeyi iptal et — borç geri açılır"
                              style={{
                                padding: '5px 12px', background: colors.dangerLight, color: '#991b1b', border: 'none',
                                borderRadius: radius.sm, cursor: iptalInFlight.has(o.id) ? 'default' : 'pointer',
                                fontSize: '12px', fontWeight: 600, opacity: iptalInFlight.has(o.id) ? 0.5 : 1, whiteSpace: 'nowrap',
                              }}>
                              İptal
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Ekstre Öde modal — erişilebilir (Escape/overlay kapatır, başlık aria) */}
      {odemeModal && (
        <div
          onClick={() => !saving && setOdemeModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Ekstre Öde"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === 'Escape' && !saving) setOdemeModal(null); }}
            style={{ background: colors.surface, borderRadius: radius.md, padding: '24px', minWidth: '360px', maxWidth: '480px', width: '100%', boxShadow: shadows.xl }}>
            <h3 style={{ marginTop: 0, marginBottom: '6px', fontSize: '17px', fontWeight: 700, color: colors.textPrimary }}>Ekstre Öde</h3>
            <p style={{ fontSize: '12px', color: colors.textSecondary, marginTop: 0, marginBottom: '16px' }}>
              Bu birikmiş kart borcunu kapatan bir nakit hareketidir. Gider olarak tekrar kaydedilmez.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
                  Dönem (kesim) tarihi
                </label>
                <input
                  type="date"
                  value={odemeModal.donem_bitis}
                  max={todayISO()}
                  onChange={e => setOdemeModal({ ...odemeModal, donem_bitis: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
                <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Önerilen: {fmtTarih(detay?.onerilen_kesim)} — banka ekstresine göre düzenleyebilirsiniz.
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
                  Ödenen tutar (TL)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={odemeModal.odenen_tutar}
                  onChange={e => setOdemeModal({ ...odemeModal, odenen_tutar: e.target.value })}
                  placeholder="0,00"
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '4px' }}>
                  Sistem önerisi: {fmt(detay?.ekstre_tutari)} TL — gerçek banka tutarını girin.
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
                  Ödeme tarihi
                </label>
                <input
                  type="date"
                  value={odemeModal.odeme_tarihi}
                  onChange={e => setOdemeModal({ ...odemeModal, odeme_tarihi: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
                  Hesap / Banka etiketi (ops.)
                </label>
                <input
                  type="text"
                  value={odemeModal.hesap_etiketi}
                  onChange={e => setOdemeModal({ ...odemeModal, hesap_etiketi: e.target.value })}
                  placeholder="Garanti vadesiz, İş Bankası…"
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '4px' }}>
                  Açıklama (ops.)
                </label>
                <input
                  type="text"
                  value={odemeModal.aciklama}
                  onChange={e => setOdemeModal({ ...odemeModal, aciklama: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px' }}>
              <button
                onClick={() => setOdemeModal(null)}
                disabled={saving}
                style={{ padding: '9px 18px', background: colors.surface, color: colors.textSecondary, border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: saving ? 'default' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                Vazgeç
              </button>
              <button
                onClick={submitOdeme}
                disabled={saving}
                style={{ padding: '9px 18px', background: colors.primary, color: '#fff', border: 'none', borderRadius: radius.sm, cursor: saving ? 'default' : 'pointer', fontSize: '13px', fontWeight: 700, opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Kaydediliyor…' : 'Ödemeyi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KrediKartlariPage;
